import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth";
import { useTranslation } from "react-i18next";

interface ForgotPasswordFormProps {
  onBack: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

export const ForgotPasswordForm = ({ onBack, isSubmitting, setIsSubmitting }: ForgotPasswordFormProps) => {
  const { t } = useTranslation('auth');
  const { resetPassword } = useAuth();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    email: z.string().email(t("validation.emailInvalid")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await resetPassword(values.email, "/auth?mode=resetPassword");
      if (result.success) {
        setSuccess("forgotPassword.success");
        form.reset();
      } else {
        setError(result.error || "forgotPassword.error");
      }
    } catch (error: unknown) {
      console.error("Reset password error:", error);
      const message = error instanceof Error ? error.message : 'forgotPassword.error';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-center text-sm text-coral">
              {t(error)}
            </div>
          )}
          {success && (
            <div className="rounded-md bg-teal-soft p-3 text-center text-sm text-teal">
              {t(success)}
            </div>
          )}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("forgotPassword.email")}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("forgotPassword.emailPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t("forgotPassword.sendingReset") : t("forgotPassword.sendResetButton")}
          </Button>
        </form>
      </Form>
      <div className="mt-4 text-center">
        <Button 
          variant="link" 
          className="p-0 h-auto text-sm"
          onClick={onBack}
        >
          {t("forgotPassword.backToLogin")}
        </Button>
      </div>
    </div>
  );
};
